import jsPDF from 'jspdf';
import { Injectable, NgZone } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ShipmentDeliveryAttachment } from 'src/app/shipment/models/shipment-delivery.attachment';
import { ref, uploadBytesResumable, getDownloadURL } from '@angular/fire/storage';
import {CustomsClearanceEndpointService} from "../../clearance/clearance.service";
import {CustomsClearanceFile} from "../../clearance/models/customs-clearance-file.model";
import {SessionStorageService} from "../session-storage.service";
import {ShipmentService} from "../../shipment/shipment.service";
import { Storage } from '@angular/fire/storage';



@Injectable({
    providedIn: 'root'
})
export class GoogleCloudStorageService {

    constructor(private messageService: MessageService,
                private shipmentService: ShipmentService,
                private sessionStorageService: SessionStorageService,
                private clearanceService: CustomsClearanceEndpointService,
                private storage: Storage,
                private ngZone: NgZone) {
    }

    openCamera() {
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        fileInput.click();
    }

    onFileSelected(event: any, selectedImage: any) {
        const file: File = event.target.files[0];
        if (file) {
            selectedImage = null;
            const reader = new FileReader();
            reader.onload = (e: any) => {
                selectedImage = e.target.result;
            };
            reader.readAsDataURL(file);
        }
    }

    uploadShipmentFile(selectedFile: File, fileName: string, fileType: string, pdfUploadOngoing: boolean | null, pdfUploadProgress: number | null,
                       shipmentId: number, deliveryId: number | undefined, stopId: number | undefined, callback: ShipmentFileDownloadUrlCallback,
                       progressCallback?: UploadProgressCallback) {
        if (!selectedFile) {
            this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please select a file first.' });
            return;
        }

        pdfUploadOngoing = true;

        // Upload the selected shipment file to Firebase Storage.
        const uploadPath = this.buildShipmentUploadPath(shipmentId, deliveryId, fileName, stopId);
        const uploadTask = uploadBytesResumable(ref(this.storage, uploadPath), selectedFile, {
            contentType: selectedFile.type || this.inferContentType(fileName)
        });

        uploadTask.on('state_changed',
            (snapshot) => {
                // Observe state change events such as progress, pause, and resume
                // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                pdfUploadProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                pdfUploadProgress = Math.round(pdfUploadProgress);
                this.emitUploadProgress(progressCallback, {
                    fileName,
                    progress: pdfUploadProgress,
                    bytesTransferred: snapshot.bytesTransferred,
                    totalBytes: snapshot.totalBytes,
                    state: snapshot.state
                });
                switch (snapshot.state) {
                    case 'paused':
                        console.log('Upload is paused');
                        break;
                    case 'running':
                        console.log('Upload is running');
                        break;
                }
            },
            (error) => {
                // Handle unsuccessful uploads
                console.error("Error uploading file:", error);
                this.emitUploadProgress(progressCallback, {
                    fileName,
                    progress: pdfUploadProgress ?? 0,
                    bytesTransferred: 0,
                    totalBytes: selectedFile.size,
                    state: 'error',
                    errorCode: error?.code
                });
                this.ngZone.run(() => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'File upload failed.' }));
            },
            () => {
                // Handle successful uploads on complete
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    console.log('File available at', downloadURL);
                    this.emitUploadProgress(progressCallback, {
                        fileName,
                        progress: 100,
                        bytesTransferred: selectedFile.size,
                        totalBytes: selectedFile.size,
                        state: 'saving'
                    });
                    if (deliveryId == null)
                        this.addOrderAttachment(selectedFile, fileName, fileType, downloadURL, shipmentId, pdfUploadProgress ?? 100, pdfUploadOngoing ?? false, callback, progressCallback);
                    else this.addDeliveryAttachment(selectedFile, fileName, fileType, downloadURL, shipmentId, deliveryId, stopId, pdfUploadProgress ?? 100, pdfUploadOngoing ?? false, callback, progressCallback)
                }).catch((error) => {
                    console.error("Error getting download URL:", error);
                    this.emitUploadProgress(progressCallback, {
                        fileName,
                        progress: 100,
                        bytesTransferred: selectedFile.size,
                        totalBytes: selectedFile.size,
                        state: 'error',
                        errorCode: error?.code
                    });
                    this.ngZone.run(() => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to get download URL.' }));
                });
            }
        );
    }

    uploadClearanceFile(selectedFile: any, fileName: string, fileType: string, pdfUploadOngoing: boolean | null, pdfUploadProgress: number | null,
                        headerId: number | undefined, detailId: number | undefined, callback: ClearanceDownloadUrlCallback) {
        if (!selectedFile) {
            this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please select a file first.' });
            return;
        }

        pdfUploadOngoing = true;

        // Upload the PDF file to Firebase Storage
        const uploadTask = uploadBytesResumable(ref(this.storage, '/clearance/header/' + (detailId == null ? '' : 'detail/') + fileName), selectedFile);

        uploadTask.on('state_changed', (snapshot) => {
                // Observe state change events such as progress, pause, and resume
                // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                pdfUploadProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                pdfUploadProgress = Math.round(pdfUploadProgress);
                if (pdfUploadProgress > 10)
                    pdfUploadProgress = pdfUploadProgress - 5;
                console.log('Upload is ' + pdfUploadProgress + '% done');
                switch (snapshot.state) {
                    case 'paused':
                        console.log('Upload is paused');
                        break;
                    case 'running':
                        console.log('Upload is running');
                        break;
                }
            },
            (error) => {
                console.error("Error uploading file:", error);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'PDF upload failed.' });
            },
            () => {
                // Handle successful uploads on complete
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    console.log('File available at', downloadURL);
                    this.clearanceService.createCustomsClearanceFile({
                        headerId: headerId,
                        detailId: detailId,
                        fileType: fileType,
                        fileName: fileName,
                        fileUrl: downloadURL,
                        fileSize: selectedFile.length,
                        createdBy: this.sessionStorageService.getCustomer()?.id,
                        updatedBy: this.sessionStorageService.getCustomer()?.id,
                        createdAt: new Date(),
                        updatedAt: new Date(),
                    } as CustomsClearanceFile).subscribe({
                        next: (data) => {
                            console.log('File available at', downloadURL);
                            this.addClearanceAttachment(selectedFile, fileName, fileType, downloadURL, headerId, detailId, pdfUploadProgress ?? 100, pdfUploadOngoing ?? false, callback);
                        },
                        error: (error) => {
                            console.error("Error in create file for Clearance: ", error);
                            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to get download URL.' });
                        }
                    });
                }).catch((error) => {
                    console.error("Error getting download URL:", error);
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to get download URL.' });
                });
            }
        );
    }

    uploadImage(selectedImage: any, fileName: string, fileType: string, pdfUploadOngoing: boolean, pdfUploadProgress: number,
                shipmentId: number, deliveryId: number, callback: ShipmentFileDownloadUrlCallback) {
        if (!selectedImage) {
            this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please capture an image first.' });
            return;
        }

        pdfUploadOngoing = true;

        // Convert image to PDF
        const doc = new jsPDF();
        doc.addImage(selectedImage, 'JPEG', 0, 0, selectedImage.width, selectedImage.height); // Adjust dimensions as needed

        // Get PDF data as a Blob
        const pdfBlob = doc.output('blob');

        // Upload the PDF file to Firebase Storage
        const uploadTask = uploadBytesResumable(ref(this.storage, this.buildShipmentUploadPath(shipmentId, deliveryId, fileName)), pdfBlob);

        uploadTask.on('state_changed',
            (snapshot) => {
                // Observe state change events such as progress, pause, and resume
                // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                pdfUploadProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                pdfUploadProgress = Math.round(pdfUploadProgress);
                if (pdfUploadProgress > 10)
                    pdfUploadProgress = pdfUploadProgress - 5;
                console.log('Upload is ' + pdfUploadProgress + '% done');
                switch (snapshot.state) {
                    case 'paused':
                        console.log('Upload is paused');
                        break;
                    case 'running':
                        console.log('Upload is running');
                        break;
                }
            },
            (error) => {
                // Handle unsuccessful uploads
                console.error("Error uploading image:", error);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'PDF upload failed.' });
            },
            () => {
                // Handle successful uploads on complete
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    console.log('File available at', downloadURL);
                    if (deliveryId == null)
                        this.addOrderAttachment(selectedImage, fileName, fileType, downloadURL, shipmentId, pdfUploadProgress, pdfUploadOngoing, callback);
                    else this.addDeliveryAttachment(selectedImage, fileName, fileType, downloadURL, shipmentId, deliveryId, undefined, pdfUploadProgress, pdfUploadOngoing, callback)
                }).catch((error) => {
                    console.error("Error getting download URL:", error);
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to get download URL.' });
                });
            }
        );
    }

    addOrderAttachment(selectedImage: File, fileName: string, fileType: string, downloadURL: string,
                       shipmentId: number, pdfUploadProgress: number, pdfUploadOngoing: boolean, callback: ShipmentFileDownloadUrlCallback,
                       progressCallback?: UploadProgressCallback) {
        this.ngZone.run(() => this.messageService.add({ severity: 'success', summary: 'Success', detail: 'File uploaded successfully.' }));
        //update firestore collection to add a new document
        let jobAttachment: ShipmentDeliveryAttachment = {
            fileName: fileName,
            fileUrl: downloadURL,
            fileSize: selectedImage.size,
            fileType: fileType,
            creationDate: new Date().getTime()
        };
        this.shipmentService.addShipmentAttachments(shipmentId, [jobAttachment]).subscribe({
            next: res => {
                console.log(res);
                this.ngZone.run(() => callback(jobAttachment));
                pdfUploadProgress = 100;
                pdfUploadOngoing = false;
                this.ngZone.run(() => this.messageService.add({ severity: 'success', summary: 'Success', detail: 'File added successfully to shipment' }));
            },
            error: error => {
                console.error('Error saving shipment attachment:', error);
                this.emitUploadProgress(progressCallback, {
                    fileName,
                    progress: 100,
                    bytesTransferred: selectedImage.size,
                    totalBytes: selectedImage.size,
                    state: 'error',
                    errorCode: error?.status ? `api/${error.status}` : error?.code
                });
                this.ngZone.run(() => this.messageService.add({ severity: 'error', summary: 'Attachment failed', detail: 'File uploaded, but saving the shipment attachment failed.' }));
            }
        });
    }

    addDeliveryAttachment(selectedImage: File, fileName: string, fileType: string, downloadURL: string,
                          shipmentId: number, deliveryId: number, stopId: number | undefined, pdfUploadProgress: number, pdfUploadOngoing: boolean,
                          callback: ShipmentFileDownloadUrlCallback,
                          progressCallback?: UploadProgressCallback) {
        this.ngZone.run(() => this.messageService.add({ severity: 'success', summary: 'Success', detail: 'File uploaded successfully.' }));
        //update firestore collection to add a new document
        let jobAttachment: ShipmentDeliveryAttachment = {
            fileName: fileName,
            stopId,
            fileUrl: downloadURL,
            fileSize: selectedImage.size,
            fileType: fileType,
            creationDate: new Date().getTime()
        };
        this.shipmentService.addDeliveryAttachments(shipmentId, deliveryId, [jobAttachment]).subscribe({
            next: res => {
                console.log(res);
                this.ngZone.run(() => callback(jobAttachment));
                pdfUploadProgress = 100;
                pdfUploadOngoing = false;
                this.ngZone.run(() => this.messageService.add({ severity: 'success', summary: 'Success', detail: 'File added successfully to delivery' }));
            },
            error: error => {
                console.error('Error saving delivery attachment:', error);
                this.emitUploadProgress(progressCallback, {
                    fileName,
                    progress: 100,
                    bytesTransferred: selectedImage.size,
                    totalBytes: selectedImage.size,
                    state: 'error',
                    errorCode: error?.status ? `api/${error.status}` : error?.code
                });
                this.ngZone.run(() => this.messageService.add({ severity: 'error', summary: 'Attachment failed', detail: 'File uploaded, but saving the delivery attachment failed.' }));
            }
        });
    }

    addClearanceAttachment(selectedImage:any, fileName: string, fileType: string, downloadURL: string,
                           headerId: number | undefined, detailId: number | undefined, pdfUploadProgress: number, pdfUploadOngoing: boolean,
                           callback: ClearanceDownloadUrlCallback) {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'PDF uploaded successfully.' });
        //update firestore collection to add a new document
        let customsClearanceFile: CustomsClearanceFile = {
            headerId: headerId,
            detailId: detailId,
            fileName: fileName,
            fileUrl: downloadURL,
            fileSize: selectedImage.length,
            fileType: fileType,
            createdAt: new Date(),
            updatedAt: new Date(),
            createdBy: this.sessionStorageService.getCustomer()?.id,
            updatedBy: this.sessionStorageService.getCustomer()?.id
        };
        this.clearanceService.createCustomsClearanceFile(customsClearanceFile).subscribe(res => {
            console.log(res);
            if (res.fileId) {
                callback(res);
                pdfUploadProgress = 100;
                pdfUploadOngoing = false;
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'PDF added successfully to delivery' });
            }
        });
    }

    private buildShipmentUploadPath(shipmentId: number, deliveryId: number | undefined, fileName: string, stopId?: number): string {
        const scope = deliveryId == null
            ? `shipments/${shipmentId}/order`
            : stopId == null
                ? `shipments/${shipmentId}/deliveries/${deliveryId}`
                : `shipments/${shipmentId}/deliveries/${deliveryId}/stops/${stopId}`;
        return `${scope}/${crypto.randomUUID()}_${this.safeStorageFileName(fileName)}`;
    }

    private safeStorageFileName(fileName: string): string {
        return fileName.replace(/[^\w.\-]+/g, '_');
    }

    private inferContentType(fileName: string): string {
        const extension = fileName.toLowerCase().split('.').pop();
        switch (extension) {
            case 'pdf':
                return 'application/pdf';
            case 'jpg':
            case 'jpeg':
                return 'image/jpeg';
            case 'png':
                return 'image/png';
            case 'gif':
                return 'image/gif';
            case 'webp':
                return 'image/webp';
            case 'heic':
                return 'image/heic';
            case 'heif':
                return 'image/heif';
            case 'mp4':
                return 'video/mp4';
            case 'mov':
                return 'video/quicktime';
            case 'avi':
                return 'video/x-msvideo';
            case 'mkv':
                return 'video/x-matroska';
            case 'webm':
                return 'video/webm';
            case 'doc':
                return 'application/msword';
            case 'docx':
                return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            case 'xls':
                return 'application/vnd.ms-excel';
            case 'xlsx':
                return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            case 'ppt':
                return 'application/vnd.ms-powerpoint';
            case 'pptx':
                return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
            case 'csv':
                return 'text/csv';
            case 'txt':
                return 'text/plain';
            case 'rtf':
                return 'application/rtf';
            default:
                return 'application/octet-stream';
        }
    }

    private emitUploadProgress(callback: UploadProgressCallback | undefined, progress: ShipmentUploadProgress): void {
        if (!callback) return;
        this.ngZone.run(() => callback(progress));
    }

}

export interface ShipmentFileDownloadUrlCallback {
    (jobAttachment: ShipmentDeliveryAttachment, batchComplete?: boolean): void | boolean | Promise<void | boolean>;
}

export interface ClearanceDownloadUrlCallback {
    (clearanceFile: CustomsClearanceFile): void;
}

export interface ShipmentUploadProgress {
    fileName: string;
    progress: number;
    bytesTransferred: number;
    totalBytes: number;
    state: string;
    errorCode?: string;
}

export interface UploadProgressCallback {
    (progress: ShipmentUploadProgress): void;
}
